import { useState } from "react";
import { accountService, homeService, taskService } from "@miplatform/api";
import { AutocompleteOption } from "@miplatform/components";
import {
  ALL,
  HOME_PHASE_OPTIONS,
  HTTP_STATUSES,
  MAX_PAGE_SIZE,
  NONE,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  AccountType,
  IAccountResponse,
  ICommunityListItemResponse,
  IOrderTemplateResponse,
} from "@miplatform/types";
import { HomeFilter } from "../types";
import { useHomeSearchParams } from "./useHomeSearchParams";

const initialFilters: HomeFilter = {
  groupBy: "phase",
  sortBy: NONE,
  builderId: ALL,
  communityId: ALL,
  phase: ALL,
  latestTemplateName: ALL,
};

export const useHomeFilters = () => {
  const stateManager = useComponentState();
  const { getFiltersFromSearchParams, searchParams, setSearchParams } =
    useHomeSearchParams();

  const [filters, setFilters] = useState<HomeFilter>(
    getFiltersFromSearchParams() || initialFilters
  );
  const [communityOptions, setCommunityOptions] = useState<
    AutocompleteOption[]
  >([]);
  const [builderOptions, setBuilderOptions] = useState<AutocompleteOption[]>(
    []
  );
  const [orderTemplateOptions, setOrderTemplateOptions] = useState<
    AutocompleteOption[]
  >([]);

  const getCommunities = async (): Promise<void> => {
    await stateManager.run(async () => {
      const { status, data } = await homeService.communities.getAll({
        sort: "name",
      });

      if (status === HTTP_STATUSES.OK) {
        setCommunityOptions([
          { label: "All", value: "All" },
          ...data.map((community: ICommunityListItemResponse) => ({
            label: community.name,
            value: community.id,
          })),
        ]);
      }
    });
  };

  const getBuilders = async (): Promise<void> => {
    await stateManager.run(async () => {
      const { status, data } = await accountService.accounts.getAll({
        sort: "name",
        top: MAX_PAGE_SIZE,
        filters: {
          accountType: {
            in: [AccountType.Builder, AccountType.Service],
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        setBuilderOptions([
          { label: "All", value: "All" },
          ...data.map((customer: IAccountResponse) => ({
            label: customer.name,
            value: customer.id,
          })),
        ]);
      }
    });
  };

  const getOrderTemplateOptions = (): Promise<void> => {
    return stateManager.run(async () => {
      const response = await taskService.orderTemplates.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        const result = response.data.map(
          (template: IOrderTemplateResponse) => ({
            value: template.name,
            label: template.name,
          })
        );
        setOrderTemplateOptions([{ label: "All", value: "All" }, ...result]);
      }
    });
  };

  const handleFilterApply = (params: HomeFilter) => {
    setFilters(params);
  };

  const resetFilters = () => {
    setFilters((prev) => ({ ...prev, ...initialFilters }));
  };

  const isFilterDirty = (newFilters: HomeFilter) =>
    JSON.stringify(newFilters) !== JSON.stringify(initialFilters);

  return {
    filters,
    setFilters,
    handleFilterApply,
    resetFilters,
    homePhaseOptions: HOME_PHASE_OPTIONS,
    builderOptions,
    communityOptions,
    orderTemplateOptions,
    getCommunities,
    getBuilders,
    getOrderTemplateOptions,
    isFilterDirty,
    searchParams,
    setSearchParams,
    isLoading: stateManager.isBusy(),
  };
};
